/**
 * Change password.
 *
 * Two ways in, and the page says which: a forced reset, where a seeded account is being refused by
 * every other endpoint until it picks a new password, or a voluntary change from the sidebar. The
 * OpenSilver view model branched on the same flag for the same reason - somebody sent here has to be
 * told why, and somebody who chose to come here must not be told their account is in a bad state.
 *
 * This is also the only place in the product where a password is set, which is why the knowledge base
 * and the forum link here rather than carrying their own form.
 */
import { insets, type Container } from 'webwin';
import * as auth from '../api/auth.js';
import * as router from '../router.js';
import { StatusLine } from '../ui/StatusLine.js';
import {
  button,
  capWidth,
  card,
  field,
  GAP,
  h1,
  muted,
  passwordBox,
  stackOf,
  textBox,
  vstack
} from '../ui/layout.js';
import type { CrmPage, PageContext } from './page.js';

export function changePasswordPage(): CrmPage {
  return {
    mount(host: Container, ctx: PageContext) {
      // Read once, on entry: submitting clears the flag, and the headline must not change under the
      // reader between the click and the answer.
      const forced = auth.mustChangePassword();

      const status = new StatusLine();
      // Read-only and shown, not hidden: a password manager decides which saved login a new password
      // replaces from the username field beside it, and skips a field it judges nobody can see.
      const account = textBox({ text: auth.currentUser()?.email ?? '', readOnly: true });
      const current = passwordBox();
      const next = passwordBox();
      const confirm = passwordBox();
      let busy = false;

      // Real inputs over the canvas fields, as on the sign-in page: the manager fills the current
      // password, offers to generate the new one, and is handed the result once the change succeeds.
      const autofill = ctx.desktop.beginAutofill([
        { control: account, purpose: 'username' },
        { control: current, purpose: 'current-password' },
        { control: next, purpose: 'new-password' },
        { control: confirm, purpose: 'new-password' }
      ]);
      ctx.dispose.add(() => autofill.dispose());

      const submit = async (): Promise<void> => {
        if (busy) return;
        status.clear();

        // Checked here as well as server-side, so the common mistakes cost no round trip and the
        // message names the actual problem rather than arriving as a generic refusal.
        if (next.text.length < 8) {
          status.error('New password must be at least 8 characters.');
          return;
        }
        if (next.text !== confirm.text) {
          status.error('The new passwords do not match.');
          return;
        }

        busy = true;
        status.busy('Updating…');
        try {
          const error = await auth.changePassword(current.text, next.text);
          if (ctx.cancelled()) return;
          if (error !== null) {
            status.error(error);
            return;
          }
          status.clear();
          void autofill.save();
          router.start();
        } finally {
          busy = false;
        }
      };

      confirm.on('keydown', e => {
        if (e.key === 'Enter') void submit();
      });

      const form = card({ hAlign: 'center', vAlign: 'center', padding: insets(24), width: 380 });
      form.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          h1(forced ? 'Choose a new password' : 'Change password'),
          muted(
            forced
              ? 'This account is still using its setup password. Choose a new one to continue.'
              : 'Enter your current password and a new one.'
          ),
          field('Account', account),
          field('Current password', current),
          field('New password', next),
          field('Confirm new password', confirm),
          status.label,
          button('Update password', () => void submit(), true, {
            hAlign: 'stretch',
            autoWidth: false
          })
        )
      );

      host.controls.add(form);
      capWidth(host, form, 380);
      current.focus();
    }
  };
}
